import { useEffect, useId, useState } from "react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import type { HttpError } from "ra-core";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { APP_LOCALE } from "../misc/locale";
import type { CrmDataProvider } from "../providers/types";
import type { AdvisorGoal, AdvisorGoalScope } from "../types";
import type { GoalAudienceMember } from "./goalsAudience";

/** PostgREST's "insufficient privilege" error code, raised by an RLS refusal. */
const POSTGREST_RLS_ERROR_CODE = "42501";

/**
 * Distinguishes an RLS refusal (acceptance criteria: "an RLS refusal shows a
 * friendly Spanish error instead of failing silently") from any other save
 * failure (network error, bigint overflow, a server error) -- the UI already
 * only renders a row a role is scoped to (`GoalsPage`'s audience helpers
 * mirror TASK-087's RLS), so an actual refusal here means a stale scope
 * (e.g. a lead_manager's brazo changed after the page loaded), not a normal
 * user mistake. Never the raw Postgres/RLS error text.
 */
function isRlsDenial(error: unknown): boolean {
  const httpError = error as Partial<HttpError> | null | undefined;
  if (!httpError) return false;
  if (httpError.status === 401 || httpError.status === 403) return true;
  const body = httpError.body as { code?: string } | null | undefined;
  return body?.code === POSTGREST_RLS_ERROR_CODE;
}

interface ParsedTarget {
  value: number | null;
  isValid: boolean;
}

/**
 * Parses one target field: empty clears the goal (both targets null =
 * "Sin meta definida", never a delete -- HARD CONSTRAINTS); anything else
 * must be a non-negative integer (DATA CONTRACT E7: target_units is
 * `integer >= 0`, target_amount_mxn is `bigint >= 0`).
 */
export function parseGoalTarget(raw: string): ParsedTarget {
  const trimmed = raw.trim();
  if (trimmed === "") return { value: null, isValid: true };
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return { value: null, isValid: false };
  }
  return { value: parsed, isValid: true };
}

export interface GoalRowInputProps {
  member: GoalAudienceMember;
  scope: AdvisorGoalScope;
  /** This period's existing goal row for `member`/`scope`, if any. */
  goal?: AdvisorGoal;
  /** First day of the selected month (`YYYY-MM-01`), matching `period_month`. */
  monthKey: string;
  /** Refetches the month's goals after a successful save. */
  onSaved: () => void;
  /**
   * Properties this advisor captured in the month (EC2d, TASK-139): her own figure, shown beside
   * the captures target. Undefined hides it (team rows, or a count the page could not complete).
   */
  capturedCount?: number;
}

/**
 * One editable "Metas" row (Almikia Epic E7, TASK-091): `target_units` /
 * `target_amount_mxn` for one advisor/month, or one Líder BPO's team goal.
 * Never issues a DELETE -- clearing both targets is an update to NULL/NULL
 * (HARD CONSTRAINTS: no hard-delete on any E7 table). Server authority
 * (RLS scope, `set_by` anti-spoofing) is entirely TASK-087/088's; this only
 * mirrors the UI-visible outcome and surfaces a friendly message on refusal.
 */
export const GoalRowInput = ({
  member,
  scope,
  goal,
  monthKey,
  onSaved,
  capturedCount,
}: GoalRowInputProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const unitsId = useId();
  const amountId = useId();
  const capturesId = useId();

  const savedUnits = goal?.target_units?.toString() ?? "";
  const savedAmount = goal?.target_amount_mxn?.toString() ?? "";
  const savedCaptures = goal?.target_captures?.toString() ?? "";

  const [unitsInput, setUnitsInput] = useState(savedUnits);
  const [amountInput, setAmountInput] = useState(savedAmount);
  const [capturesInput, setCapturesInput] = useState(savedCaptures);
  const [validationError, setValidationError] = useState<string | null>(null);

  // A goal/month change (saved elsewhere, or a different month picked) must
  // reset this row to that period's saved values, not keep stale input from
  // whatever was previously selected.
  useEffect(() => {
    setUnitsInput(savedUnits);
    setAmountInput(savedAmount);
    setCapturesInput(savedCaptures);
    setValidationError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedUnits, savedAmount, savedCaptures, monthKey]);

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: (data: {
      target_units: number | null;
      target_amount_mxn: number | null;
      target_captures: number | null;
    }) => {
      if (goal) {
        return dataProvider.update("advisor_goals", {
          id: goal.id,
          data,
          previousData: goal,
        });
      }
      return dataProvider.create("advisor_goals", {
        data: {
          sales_id: member.id,
          scope,
          period_month: monthKey,
          ...data,
        },
      });
    },
    onSuccess: () => {
      notify("crm.goals.save_success", { type: "success" });
      onSaved();
    },
    onError: (error) => {
      notify(
        isRlsDenial(error)
          ? "crm.goals.error.save_denied"
          : "crm.goals.error.save_failed",
        { type: "error" },
      );
    },
  });

  const isDirty =
    unitsInput !== savedUnits ||
    amountInput !== savedAmount ||
    capturesInput !== savedCaptures;
  const isEmpty =
    unitsInput.trim() === "" &&
    amountInput.trim() === "" &&
    capturesInput.trim() === "";

  // MXN readout next to the raw digits (acceptance criteria: "amounts show
  // as MXN with tabular numbers") -- only rendered for a valid, non-empty
  // amount; an invalid entry is reported by the validation message instead.
  const parsedAmount = parseGoalTarget(amountInput);
  const formattedAmount =
    parsedAmount.isValid && parsedAmount.value != null
      ? parsedAmount.value.toLocaleString(APP_LOCALE, {
          style: "currency",
          currency: "MXN",
          maximumFractionDigits: 0,
        })
      : null;

  const handleSave = () => {
    const units = parseGoalTarget(unitsInput);
    const amount = parseGoalTarget(amountInput);
    const captures = parseGoalTarget(capturesInput);
    if (!units.isValid || !amount.isValid || !captures.isValid) {
      setValidationError(translate("crm.goals.error.invalid_target"));
      return;
    }
    if (
      !goal &&
      units.value == null &&
      amount.value == null &&
      captures.value == null
    ) {
      // Nothing to create: an all-empty row with no prior goal stays "Sin
      // meta definida" without writing an empty row.
      return;
    }
    setValidationError(null);
    save({
      target_units: units.value,
      target_amount_mxn: amount.value,
      target_captures: captures.value,
    });
  };

  return (
    <div
      role="group"
      aria-label={`${member.first_name} ${member.last_name}`}
      className="flex flex-col gap-3 rounded-2xl border border-border/60 p-4 sm:flex-row sm:flex-wrap sm:items-end"
    >
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold">
          {member.first_name} {member.last_name}
        </p>
        {member.advisor_code ? (
          <p className="text-sm text-muted-foreground">{member.advisor_code}</p>
        ) : null}
        {isEmpty ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.goals.no_goal")}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={unitsId}>
          {translate("crm.goals.fields.target_units")}
        </Label>
        <Input
          id={unitsId}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          className="h-11 w-28 tabular-nums"
          value={unitsInput}
          onChange={(event) => setUnitsInput(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={amountId}>
          {translate("crm.goals.fields.target_amount_mxn")}
        </Label>
        <Input
          id={amountId}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          className="h-11 w-36 tabular-nums"
          value={amountInput}
          onChange={(event) => setAmountInput(event.target.value)}
        />
        {formattedAmount ? (
          <p className="text-sm text-muted-foreground tabular-nums">
            {formattedAmount}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={capturesId}>
          {translate("crm.goals.fields.target_captures")}
        </Label>
        <Input
          id={capturesId}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          className="h-11 w-28 tabular-nums"
          value={capturesInput}
          onChange={(event) => setCapturesInput(event.target.value)}
        />
        {capturedCount != null ? (
          <p className="text-sm text-muted-foreground tabular-nums">
            {translate("crm.goals.captured_this_month", {
              smart_count: capturedCount,
            })}
          </p>
        ) : null}
      </div>
      <Button
        type="button"
        size="sm"
        className="h-11"
        disabled={!isDirty || isSaving}
        onClick={handleSave}
      >
        {translate("crm.goals.action.save")}
      </Button>
      {validationError ? (
        <p role="alert" className="w-full text-sm text-destructive">
          {validationError}
        </p>
      ) : null}
    </div>
  );
};
